import React, {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import SearchPanel from "@widgets/SearchPanel";
import { fnGetDataSet } from "@shared/api/restApi";
import { getInitPopUpData } from "@shared/utils/CommonUtils";
import { global } from "@shared/libs/Global";
import { SnGridRef } from "@shared/components/GridV2/SnGridTypes";
import SnGrid from "@shared/components/GridV2/SnGrid";
import SnSearchBtn from "@shared/components/SnSearchBtn";
import SnResizeBar from "@shared/components/SnResizeBar";
import SnTab from "@shared/components/SnTab";
import Button from "@mui/material/Button";
import { Badge, IconButton, Typography } from "@mui/material";
import { ArrowLeftIcon } from "@mui/x-date-pickers";
import { v4 as uuidv4 } from "uuid";

interface popupProps {
  selectedData?: any;
  modalRef?: any;
}

const SN_MTL_MPI_REG_002_POP_003 = forwardRef(
  ({ selectedData, modalRef }: popupProps, ref: React.Ref<any>) => {
    const { userInfo } = global();
    const searchRef = useRef<any>(null);
    const activeTabRef = useRef<string>("1"); // "1": 출고정보, "2": 출고번호별

    const gridRefs = useRef<{ [key: string]: React.RefObject<SnGridRef> }>({
      grid1: React.createRef<SnGridRef>(),
      grid2: React.createRef<SnGridRef>(),
      grid3: React.createRef<SnGridRef>(),
      cart: React.createRef<SnGridRef>(),
    });

    const getGridCurrent = (key: string): SnGridRef => {
      const grid = gridRefs.current[key]?.current;
      if (!grid) {
        throw new Error(`Grid '${key}' is not mounted yet`);
      }
      return grid;
    };

    const [formData, setFormData] = useState<any>();
    const [formControlData, setFormControlData] = useState<any[]>([]);
    const [columns, setColums] = useState<any>({
      grid1: [],
      grid2: [],
      grid3: [],
    });
    const [dataSources, setDataSources] = useState<any>({
      grid1: [],
      grid2: [],
      grid3: [],
    });

    // 장바구니(선택 담기) — 출고정보/출고번호별 두 탭에서 체크행을 담는다.
    const [cart, setCart] = useState<any[]>([]);
    const cartRef = useRef<any[]>([]);
    const [drawerOpen, setDrawerOpen] = useState(false);
    useEffect(() => {
      cartRef.current = cart;
    }, [cart]);

    // 같은 출고라인 중복 담기 방지 키 (출고전표/항번/LOT항번/바코드)
    const cartKeyOf = (r: any) =>
      `${r?.no_io ?? ""}|${r?.no_line ?? ""}|${r?.no_line_lot ?? ""}|${
        r?.no_barcode ?? ""
      }`;

    // 담기: 현재 탭(출고정보=grid1 / 출고번호별=grid3)의 체크행을 장바구니에 추가
    const addToCart = () => {
      const key = activeTabRef.current === "1" ? "grid1" : "grid3";
      const checked = getGridCurrent(key).getCheckedRows?.() ?? [];
      if (!checked.length) return;
      setCart((prev) => {
        const existing = new Set(prev.map(cartKeyOf));
        const toAdd = checked
          .filter((r: any) => !existing.has(cartKeyOf(r)))
          .map((r: any) => ({ ...r, KeyField: uuidv4() }));
        return toAdd.length ? [...prev, ...toAdd] : prev;
      });
    };

    const removeFromCart = (rowData: any) => {
      if (!rowData) return;
      setCart((prev) => prev.filter((row) => row.KeyField !== rowData.KeyField));
    };

    const removeCartAll = () => setCart([]);

    useImperativeHandle(ref, () => ({
      // 확인 반환 = 장바구니 기준. 부모(btn_POP3)는 result[1](라인)만 사용.
      getOkayData: () => ({
        rtnData: [
          [],
          cartRef.current.map(({ KeyField, ...rest }: any) => rest),
        ],
      }),
    }));

    useEffect(() => {
      const fetchform = async () => {
        try {
          const rtn: any = await getInitPopUpData(
            "SN_MTL_MPI_REG_002_POP_003",
            {}
          );
          const mergedData: any = { ...rtn.formData, ...selectedData };
          setFormControlData(rtn.formControlData);
          setFormData(mergedData);
          setColums({
            grid1: rtn.grid.grid1,
            grid2: rtn.grid.grid2,
            grid3: rtn.grid.grid3,
          });
        } catch (error) {}
      };
      fetchform();
    }, []);

    // 장바구니 그리드 컬럼 = 삭제버튼 + 출고정보(grid1) 컬럼
    const cartColumns = useMemo(() => {
      const deleteBtn: any = {
        cd_firm: userInfo.cd_firm,
        cd_menu: "SN_MTL_MPI_REG_002_POP_003",
        cd_grid: "cart",
        cd_user: "admin",
        cd_column: "btn_delete",
        nm_column: "",
        tp_column: "SnButtonBox",
        fg_visible: "Y",
        cd_displaycolumn: null,
        cd_clas: null,
        cd_popup: null,
        no_seq: 0,
        qt_width: 40,
        yn_required: "N",
        yn_allowsorting: "Y",
        yn_fixed: "N",
        tp_fixedposition: null,
        qt_groupindex: -1,
        yn_sum: "N",
        yn_system: "N",
        tp_format: null,
        dc_editcon: null,
        yn_drilldown: "N",
        dc_sql: null,
        dc_filter: null,
        headerComponentParams: { iconType: "btn_delete", onClick: removeCartAll },
        cellRendererParams: { iconType: "btn_delete", onClick: removeFromCart },
      };
      return [deleteBtn, ...(columns.grid1 || [])];
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [columns.grid1]);

    const onSearch = async () => {
      try {
        const param = {
          id: "sn_mtl_mpi_reg_002_pop_003_01",
          params: searchRef.current.getFormdata(),
        };
        getGridCurrent("grid1").resetUpdate();
        getGridCurrent("grid2").resetUpdate();
        getGridCurrent("grid3").resetUpdate();
        const data: any = await fnGetDataSet(param);

        setDataSources({
          grid1: data[0] || [],
          grid2: data[1] || [],
          grid3: data[2] || [],
        });
      } catch (error: any) {}
    };

    const onCheckedRowChaged2 = (
      value: boolean,
      type: "row" | "header" = "row"
    ) => {
      if (type === "header") {
        getGridCurrent("grid3").setCheckedAll(value);
      } else {
        getGridCurrent("grid3").setCheckedAllWithFilter(value);
      }
    };

    const onCheckedRowChaged3 = (value: boolean) => {
      const checkedRows = getGridCurrent("grid3").getCheckedFilteredRows();
      if (checkedRows.length > 0)
        getGridCurrent("grid2").setCheckedFocusedRow(true, "noEvent");
      else getGridCurrent("grid2").setCheckedFocusedRow(false, "noEvent");
    };

    const grids = useMemo(
      () => ({
        grid1: {
          columns: columns.grid1,
          dataSource: dataSources.grid1,
        },
        grid2: {
          columns: columns.grid2,
          dataSource: dataSources.grid2,
          events: {
            onCheckedRowChaged: onCheckedRowChaged2,
          },
        },
        grid3: {
          columns: columns.grid3,
          dataSource: dataSources.grid3,
          events: {
            onCheckedRowChaged: onCheckedRowChaged3,
          },
        },
      }),
      [columns, dataSources]
    );

    return (
      <div style={{ position: "relative" }}>
        <div>
          <SearchPanel
            ref={searchRef}
            data={formData}
            controls={formControlData}
            popupRef={ref}
          ></SearchPanel>
        </div>
        {/* 담기 / 조회 / 장바구니 토글 */}
        <div
          style={{
            position: "relative",
            display: "flex",
            alignItems: "center",
            marginTop: 8,
          }}
        >
          <div style={{ display: "flex", alignItems: "center" }}>
            <Button
              type="button"
              variant="outlined"
              onClick={addToCart}
              sx={{ minWidth: 0, height: 32, marginLeft: 1, paddingX: 1.5 }}
            >
              담기
            </Button>
          </div>
          <div
            style={{
              position: "absolute",
              left: "50%",
              transform: "translateX(-50%)",
            }}
          >
            <SnSearchBtn
              text={"조회"}
              onClick={onSearch}
              sx={{ width: 80 }}
              spinner={true}
            />
          </div>
          {!drawerOpen && (
            <div
              style={{
                flex: 1,
                textAlign: "right",
                marginRight: cart.length > 0 ? "20px" : "0px",
              }}
            >
              <Badge
                badgeContent={cart.length}
                color="error"
                overlap="circular"
                showZero={false}
                anchorOrigin={{ vertical: "top", horizontal: "right" }}
                sx={{
                  "& .MuiBadge-badge": {
                    transform: "translate(100%, -80%)",
                  },
                }}
              >
                <IconButton
                  onClick={() => setDrawerOpen(true)}
                  sx={{
                    width: 24,
                    height: 24,
                    border: "1px solid #ccc",
                    background: "#fff",
                  }}
                >
                  <ArrowLeftIcon fontSize="small" />
                </IconButton>
              </Badge>
            </div>
          )}
        </div>
        <div>
          <div style={{ height: "55vh", marginTop: "10px" }}>
            <SnTab
              titles={["출고정보", "출고번호별"]}
              width={"100%"}
              onTabChanged={(index) => {
                activeTabRef.current = index;
              }}
              body={[
                <SnGrid
                  ref={gridRefs.current["grid1"]}
                  columnConfig={grids.grid1.columns}
                  rowData={grids.grid1.dataSource}
                  etcProps={{
                    rowSelection: "multiple",
                    isEditable: false,
                    ischeckboxSelection: true,
                  }}
                />,
                <div
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "10px",
                    height: "100%",
                    width: "100%",
                  }}
                >
                  <SnResizeBar direction={"height"} id="panel1" orgSize="50%">
                    <SnGrid
                      ref={gridRefs.current["grid2"]}
                      columnConfig={grids.grid2.columns}
                      rowData={grids.grid2.dataSource}
                      etcProps={{
                        rowSelection: "multiple",
                        isEditable: false,
                        ischeckboxSelection: true,
                      }}
                      events={grids.grid2.events}
                      filter={{
                        refs: [gridRefs.current["grid3"]],
                        columns: ["no_io"],
                      }}
                    />
                  </SnResizeBar>
                  <div className="flex-1 min-h-0">
                    <SnGrid
                      ref={gridRefs.current["grid3"]}
                      columnConfig={grids.grid3.columns}
                      rowData={grids.grid3.dataSource}
                      events={grids.grid3.events}
                      etcProps={{
                        rowSelection: "multiple",
                        isEditable: false,
                        ischeckboxSelection: true,
                      }}
                    />
                  </div>
                </div>,
              ]}
            />
          </div>
        </div>

        {/* 장바구니 패널 */}
        {drawerOpen && (
          <>
            <div
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                height: "100%",
                width: 420,
                backgroundColor: "#fff",
                display: "flex",
                flexDirection: "column",
                zIndex: 10,
                padding: 10,
                border: "1px solid rgb(186,191,199)",
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <Typography variant="h6">장바구니</Typography>
              <div style={{ flex: 1, minHeight: 0, marginTop: 5 }}>
                <SnGrid
                  ref={gridRefs.current["cart"]}
                  columnConfig={cartColumns}
                  rowData={cart}
                  etcProps={{ rowSelection: "multiple", isEditable: false }}
                />
              </div>
            </div>
            <div
              onClick={() => setDrawerOpen(false)}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 420,
                bottom: 0,
                zIndex: 9,
              }}
            />
          </>
        )}
      </div>
    );
  }
);

export default SN_MTL_MPI_REG_002_POP_003;
